import Button from "@/components/bricks/button/Button";
import { useConfirm } from "@/components/confirm/useConfirm";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon";
import DeleteIcon from "@/components/icons/DeleteIcon";
import DownloadIcon from "@/components/icons/DownloadIcon";
import Spinner from "@/components/spinner/Spinner";
import { UserQueryContext } from "@/context/UserContext";
import { useDownloadInjunctionDecision } from "@/hooks/cle-hooks";
import {
  useDeletePersonPaalaeg,
  usePersonPaalaeg,
} from "@/hooks/person-hooks/usePersonPaalaeg";
import { formatLessons } from "@/util/number";
import { useContext, useMemo, type ReactNode } from "react";

import type { DecoratedPersonEfteruddannelse } from "./decoratePeriod";
import { EditPaalaegModal } from "./EditPaalaegModal";
import { findTransferredLinjer } from "./injunctionLessonTypeUtils";
import { PaalaegDocumentAccordions } from "./PaalaegDocumentAccordions";
import { PaalaegLessonTypeSummaryCard } from "./PaalaegLessonTypeSummaryCard";
import {
  mapIndberetningStatusBadgeVariant,
  mapIndberetningStatusLabel,
} from "./paalaegUtils";
import { StatusBadge } from "./StatusBadge";
import {
  formaterFrist,
  formaterIndfrielsesperiode,
  formaterResterendeTid,
} from "./injunctionPeriode";
import { formatDate, formatOverfoertTilPeriode } from "./utils/formatters";

interface PaalaegDetailViewProps {
  personId: string;
  paalaegId: string;
  allPeriods: DecoratedPersonEfteruddannelse[];
  onBack: () => void;
  onSelectCertificate: (documentId: string) => void;
  onSelectReduction: (reductionId: string) => void;
  onSelectLegalAid: (legalAidId: string) => void;
  onSelectAuthorship: (authorshipId: string) => void;
  onSelectTeaching: (teachingId: string) => void;
}

const SummaryField = ({
  label,
  value,
  className,
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) => (
  <div className={className ?? "col-xl-3 col-md-6"}>
    <div className="h-100 rounded border bg-light p-3">
      <div className="text-muted small mb-1">{label}</div>
      <div className="fw-semibold fs-6">{value}</div>
    </div>
  </div>
);

export const PaalaegDetailView = ({
  personId,
  paalaegId,
  allPeriods,
  onBack,
  onSelectCertificate,
  onSelectReduction,
  onSelectLegalAid,
  onSelectAuthorship,
  onSelectTeaching,
}: PaalaegDetailViewProps) => {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "TilsynWrite");
  const { confirmDialog } = useConfirm();
  const { data, isLoading, error } = usePersonPaalaeg({
    personId,
    paalaegId,
  });
  const transferredLinjer = useMemo(
    () =>
      findTransferredLinjer(
        allPeriods,
        data?.indberetning?.overfoertTilPersonEfteruddannelseIndberetningId,
      ),
    [allPeriods, data],
  );
  const { mutate: downloadInjunctionDecision, isPending: isDownloading } =
    useDownloadInjunctionDecision();
  const { mutate: deletePaalaeg, isPending: isDeleting } =
    useDeletePersonPaalaeg();

  const handleDownloadClick = () => {
    downloadInjunctionDecision({
      userId: personId,
      injunctionId: paalaegId,
    });
  };

  const handleDelete = async () => {
    const confirmed = await confirmDialog(
      "Slet pålæg",
      "Er du sikker på, at du vil slette dette pålæg? Handlingen kan ikke fortrydes.",
      "Annuller",
      "Slet",
    );

    if (!confirmed) return;

    deletePaalaeg(
      { personId, paalaegId },
      {
        onSuccess: () => onBack(),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="container">
        <Spinner label="Henter pålæg ..." />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container">
        <Button
          id="efteruddannelse-paalaeg-back"
          color="secondary"
          transparent
          type="button"
          className="ps-0 mb-3"
          onClick={onBack}
        >
          <ChevronLeftIcon />
          Tilbage til efteruddannelse
        </Button>
        <div className="text-danger">Kunne ikke hente pålæg.</div>
      </div>
    );
  }

  const title =
    data.decisionDocumentFileName ??
    `${formatLessons(data.paakraevedeLektioner)} lektioner`;
  const indberetningStatus =
    data.indberetning?.efteruddannelseIndberetningStatus;
  const indberetningStatusLabel =
    mapIndberetningStatusLabel(indberetningStatus);
  const hasBemaerkning = Boolean(data.bemaerkning?.trim());

  return (
    <div className="container pb-4">
      <div className="mb-3">
        <Button
          id="efteruddannelse-paalaeg-back"
          color="secondary"
          transparent
          type="button"
          className="ps-0"
          onClick={onBack}
        >
          <ChevronLeftIcon />
          Tilbage til efteruddannelse
        </Button>
      </div>
      <div className="c-card p-4 mb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <div className="text-muted small mb-1">Pålæg</div>
            <h2 className="h3 mb-0">{title}</h2>
          </div>

          <div className="d-flex flex-wrap gap-2 align-items-center flex-shrink-0">
            {canWrite && (
              <>
                <EditPaalaegModal personId={personId} paalaegId={paalaegId} />
                {data.status !== "Slettet" && (
                  <Button
                    color="secondary"
                    disabled={isDeleting || isDownloading}
                    id="efteruddannelse-paalaeg-delete"
                    type="button"
                    onClick={handleDelete}
                  >
                    {isDeleting ? (
                      <Spinner size={14} />
                    ) : (
                      <DeleteIcon height={16} />
                    )}
                    Slet
                  </Button>
                )}
              </>
            )}
            <Button
              color="-action"
              disabled={isDownloading || isDeleting}
              id="efteruddannelse-paalaeg-download"
              type="button"
              onClick={handleDownloadClick}
            >
              {isDownloading ? (
                <Spinner size={14} />
              ) : (
                <DownloadIcon height={16} />
              )}
              Hent kendelse
            </Button>
          </div>
        </div>

        <div className="row g-3">
          <SummaryField
            label="Kendelsesdato"
            value={formatDate(data.kendelseDato)}
          />
          <SummaryField
            label="Dokumentation gælder fra"
            value={formatDate(data.dokumentationGaelderFra ?? data.periodeFra)}
          />
          <SummaryField
            label="Indfrielsesperiode"
            value={formaterIndfrielsesperiode(
              data.periodeFra,
              data.periodeTil,
              formatDate,
            )}
          />
          <SummaryField
            label="Indfrielsesperiodens længde"
            value={
              data.periodeLaengdeMaaneder
                ? `${data.periodeLaengdeMaaneder} måneder`
                : "-"
            }
          />
          <SummaryField
            label="Påkrævede lektioner"
            value={formatLessons(data.paakraevedeLektioner)}
          />
          <SummaryField
            label="Gennemførte lektioner"
            value={formatLessons(data.lektionerGennemfoert)}
          />
          <SummaryField
            label="Overførte lektioner"
            value={formatLessons(
              data.lektionerOverfoert ?? data.indberetning?.lektionerOverfoert,
            )}
          />
          <SummaryField
            label="Dokumentationsfrist"
            value={formaterFrist(
              data.indberetning?.indberetningFrist,
              formatDate,
            )}
          />
          {data.erIBero ? (
            <SummaryField
              label="I bero"
              value={
                data.iBeroSiden
                  ? `Siden ${formatDate(data.iBeroSiden)} — genoptages ved genudlevering`
                  : "Afventer at advokaten er udøvende længe nok"
              }
            />
          ) : (
            <SummaryField
              label="Resterende tid"
              value={formaterResterendeTid(data.resterendeUdoevendeDage)}
            />
          )}
          <SummaryField
            label="Modtaget dato"
            value={formatDate(data.indberetning?.registreringsDato)}
          />
          {data.indberetning?.signaturNavn ? (
            <SummaryField
              label="Indberetning signeret af"
              value={data.indberetning.signaturNavn}
            />
          ) : null}
          <SummaryField
            label="Status"
            value={
              indberetningStatus ? (
                <StatusBadge
                  pill
                  status={indberetningStatusLabel}
                  variant={mapIndberetningStatusBadgeVariant(
                    indberetningStatus,
                  )}
                />
              ) : (
                "-"
              )
            }
          />
          <SummaryField label="Oprettet af" value={data.opretInit ?? "-"} />
          {data.indberetning?.overfoertTilPersonEfteruddannelseId && (
            <SummaryField
              label="Overført til periode"
              value={formatOverfoertTilPeriode(
                data.indberetning.overfoertTilPeriodeFra,
                data.indberetning.overfoertTilPeriodeTil,
                data.indberetning.overfoertTilPersonEfteruddannelseId,
              )}
            />
          )}
          {hasBemaerkning && (
            <SummaryField
              className="col-12"
              label="Bemærkning"
              value={data.bemaerkning ?? "-"}
            />
          )}
        </div>
      </div>
      {data.indberetning?.linjer?.length ? (
        <section className="mb-4">
          <PaalaegLessonTypeSummaryCard
            linjer={data.indberetning?.linjer}
            transferredLinjer={transferredLinjer}
          />
        </section>
      ) : null}
      <section className="mb-4">
        <h3 className="h5 mb-3">Indberettede lektionsdokumenter</h3>
        <PaalaegDocumentAccordions
          paalaegId={paalaegId}
          userId={personId}
          onSelectCertificate={onSelectCertificate}
          onSelectReduction={onSelectReduction}
          onSelectLegalAid={onSelectLegalAid}
          onSelectAuthorship={onSelectAuthorship}
          onSelectTeaching={onSelectTeaching}
        />
      </section>
    </div>
  );
};
